import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Smartphone, Check, Share, Plus, ArrowLeft, Leaf } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MobileHeader } from '@/components/layout/MobileHeader';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function Install() {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // Listen for install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }

    setDeferredPrompt(null);
  };

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <MobileHeader title="Instalar VerdeIA" showBack />
        
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">VerdeIA Instalado!</h1>
            <p className="text-muted-foreground mb-6">
              O VerdeIA já está instalado no seu dispositivo.
            </p>
            <Button onClick={() => navigate('/')} className="gradient-nature text-primary-foreground">
              Voltar ao Início
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MobileHeader title="Instalar VerdeIA" showBack />
      
      <main className="flex-1 p-6">
        <div className="max-w-md mx-auto">
          {/* App Icon */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 rounded-3xl bg-primary flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Leaf className="h-12 w-12 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Instalar VerdeIA</h1>
            <p className="text-muted-foreground">
              Instale o app para uma experiência completa
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Smartphone className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Acesso rápido</h3>
                <p className="text-sm text-muted-foreground">
                  Abra direto da tela inicial do seu celular
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Download className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Funciona offline</h3>
                <p className="text-sm text-muted-foreground">
                  Acesse sua coleção mesmo sem internet
                </p>
              </div>
            </div>
          </div>

          {/* Install Instructions */}
          {isIOS ? (
            <div className="space-y-4">
              <h2 className="font-semibold text-foreground">Como instalar no iPhone:</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    1
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Toque em</span>
                    <Share className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">Compartilhar</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    2
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Toque em</span>
                    <Plus className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">"Adicionar à Tela de Início"</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    3
                  </div>
                  <span className="text-sm">Confirme tocando em <strong>"Adicionar"</strong></span>
                </div>
              </div>
            </div>
          ) : deferredPrompt ? (
            <Button
              onClick={handleInstall}
              size="lg"
              className="w-full h-14 gradient-nature text-primary-foreground text-lg"
            >
              <Download className="mr-2 h-5 w-5" />
              Instalar Agora
            </Button>
          ) : (
            <div className="space-y-4">
              <h2 className="font-semibold text-foreground">Como instalar:</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    1
                  </div>
                  <span className="text-sm">Abra o menu do navegador (⋮)</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                    2
                  </div>
                  <span className="text-sm">Toque em <strong>"Instalar app"</strong> ou <strong>"Adicionar à tela inicial"</strong></span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
